/**
 * useSeasonalTheme — Auto-detects upcoming US holidays and returns
 * festive accent colors and labels for the hero section and section titles.
 * Holiday themes activate 7 days before each holiday and through the day of.
 */
import { useMemo } from 'react';

// Compute Easter Sunday using the Anonymous Gregorian algorithm (computus)
function computeEasterDate(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { month, day };
}

// Compute dynamic holidays that shift each year
function getMLKDay(year) {
  // 3rd Monday of January
  let date = new Date(year, 0, 1);
  let mondayCount = 0;
  while (mondayCount < 3) {
    if (date.getDay() === 1) mondayCount++;
    if (mondayCount < 3) date.setDate(date.getDate() + 1);
  }
  return { month: 1, day: date.getDate() };
}

function getPresidentsDay(year) {
  // 3rd Monday of February
  let date = new Date(year, 1, 1);
  let mondayCount = 0;
  while (mondayCount < 3) {
    if (date.getDay() === 1) mondayCount++;
    if (mondayCount < 3) date.setDate(date.getDate() + 1);
  }
  return { month: 2, day: date.getDate() };
}

function getMemorialDay(year) {
  // Last Monday of May
  let date = new Date(year, 4, 31);
  while (date.getDay() !== 1) date.setDate(date.getDate() - 1);
  return { month: 5, day: date.getDate() };
}

function getLaborDay(year) {
  // 1st Monday of September
  let date = new Date(year, 8, 1);
  while (date.getDay() !== 1) date.setDate(date.getDate() + 1);
  return { month: 9, day: date.getDate() };
}

function getThanksgiving(year) {
  // 4th Thursday of November
  let date = new Date(year, 10, 1);
  let thursdayCount = 0;
  while (thursdayCount < 4) {
    if (date.getDay() === 4) thursdayCount++;
    if (thursdayCount < 4) date.setDate(date.getDate() + 1);
  }
  return { month: 11, day: date.getDate() };
}

// Build US holidays list for the current year
function buildHolidays(year) {
  const easter = computeEasterDate(year);
  const mlk = getMLKDay(year);
  const presidents = getPresidentsDay(year);
  const memorial = getMemorialDay(year);
  const labor = getLaborDay(year);
  const thanksgiving = getThanksgiving(year);

  // All holidays show 7 days before, through the day of
  const RANGE = [-7, 0];

  return [
    {
      name: "New Year's",
      month: 1, day: 1, range: RANGE,
      accent: '#FFD700', secondary: '#C0C0C0',
      heroGradient: 'from-[#FFD700]/15 via-transparent to-[#C0C0C0]/10',
      label: 'HAPPY NEW YEAR',
      sectionAccent: { bg: 'rgba(255,215,0,0.12)', color: '#FFD700', border: '#FFD700' },
      lightAccent: { bg: 'rgba(184,134,11,0.1)', color: '#92400e', border: '#b8860b' }
    },
    {
      name: "MLK Day",
      month: mlk.month, day: mlk.day, range: RANGE,
      accent: '#4A90D9', secondary: '#FFFFFF',
      heroGradient: 'from-[#4A90D9]/10 via-transparent to-transparent',
      label: 'MLK DAY',
      sectionAccent: { bg: 'rgba(74,144,217,0.12)', color: '#4A90D9', border: '#4A90D9' },
      lightAccent: { bg: 'rgba(37,99,235,0.1)', color: '#1e40af', border: '#2563eb' }
    },
    {
      name: "Valentine's Day",
      month: 2, day: 14, range: RANGE,
      accent: '#FF1493', secondary: '#FF69B4',
      heroGradient: 'from-[#FF1493]/15 via-transparent to-[#FF69B4]/10',
      label: "VALENTINE'S DAY",
      sectionAccent: { bg: 'rgba(255,20,147,0.12)', color: '#FF1493', border: '#FF1493' },
      lightAccent: { bg: 'rgba(219,39,119,0.1)', color: '#be185d', border: '#ec4899' }
    },
    {
      name: "Presidents' Day",
      month: presidents.month, day: presidents.day, range: RANGE,
      accent: '#B22234', secondary: '#3C3B6E',
      heroGradient: 'from-[#B22234]/10 via-transparent to-[#3C3B6E]/10',
      label: "PRESIDENTS' DAY",
      sectionAccent: { bg: 'rgba(178,34,52,0.12)', color: '#B22234', border: '#B22234' },
      lightAccent: { bg: 'rgba(178,34,52,0.1)', color: '#991b1b', border: '#dc2626' }
    },
    {
      name: "St. Patrick's Day",
      month: 3, day: 17, range: RANGE,
      accent: '#00A86B', secondary: '#228B22',
      heroGradient: 'from-[#00A86B]/15 via-transparent to-[#228B22]/10',
      label: "ST. PATRICK'S DAY",
      sectionAccent: { bg: 'rgba(0,168,107,0.12)', color: '#00A86B', border: '#00A86B' },
      lightAccent: { bg: 'rgba(4,120,87,0.1)', color: '#047857', border: '#059669' }
    },
    {
      name: "Easter",
      month: easter.month, day: easter.day, range: RANGE,
      accent: '#DDA0DD', secondary: '#98FB98',
      heroGradient: 'from-[#DDA0DD]/15 via-transparent to-[#98FB98]/10',
      label: 'HAPPY EASTER',
      sectionAccent: { bg: 'rgba(221,160,221,0.12)', color: '#DDA0DD', border: '#DDA0DD' },
      lightAccent: { bg: 'rgba(168,85,247,0.1)', color: '#7c3aed', border: '#a855f7' }
    },
    {
      name: "Memorial Day",
      month: memorial.month, day: memorial.day, range: RANGE,
      accent: '#B22234', secondary: '#FFFFFF',
      heroGradient: 'from-[#B22234]/10 via-transparent to-[#3C3B6E]/10',
      label: 'MEMORIAL DAY',
      sectionAccent: { bg: 'rgba(178,34,52,0.12)', color: '#B22234', border: '#B22234' },
      lightAccent: { bg: 'rgba(178,34,52,0.1)', color: '#991b1b', border: '#dc2626' }
    },
    {
      name: "Juneteenth",
      month: 6, day: 19, range: RANGE,
      accent: '#E31B23', secondary: '#009A44',
      heroGradient: 'from-[#E31B23]/10 via-transparent to-[#009A44]/10',
      label: 'JUNETEENTH',
      sectionAccent: { bg: 'rgba(227,27,35,0.12)', color: '#E31B23', border: '#E31B23' },
      lightAccent: { bg: 'rgba(227,27,35,0.1)', color: '#b91c1c', border: '#dc2626' }
    },
    {
      name: "Independence Day",
      month: 7, day: 4, range: RANGE,
      accent: '#FF0000', secondary: '#0000FF',
      heroGradient: 'from-[#FF0000]/12 via-[#FFFFFF]/5 to-[#0000FF]/12',
      label: 'HAPPY 4TH OF JULY',
      sectionAccent: { bg: 'rgba(255,0,0,0.12)', color: '#FF4444', border: '#FF0000' },
      lightAccent: { bg: 'rgba(37,99,235,0.1)', color: '#1e40af', border: '#2563eb' }
    },
    {
      name: "Labor Day",
      month: labor.month, day: labor.day, range: RANGE,
      accent: '#4169E1', secondary: '#DC143C',
      heroGradient: 'from-[#4169E1]/10 via-transparent to-[#DC143C]/10',
      label: 'LABOR DAY',
      sectionAccent: { bg: 'rgba(65,105,225,0.12)', color: '#4169E1', border: '#4169E1' },
      lightAccent: { bg: 'rgba(37,99,235,0.1)', color: '#1e40af', border: '#2563eb' }
    },
    {
      name: "Halloween",
      month: 10, day: 31, range: RANGE,
      accent: '#FF6600', secondary: '#800080',
      heroGradient: 'from-[#FF6600]/15 via-transparent to-[#800080]/12',
      label: 'HAPPY HALLOWEEN',
      sectionAccent: { bg: 'rgba(255,102,0,0.15)', color: '#FF6600', border: '#FF6600' },
      lightAccent: { bg: 'rgba(234,88,12,0.1)', color: '#c2410c', border: '#ea580c' }
    },
    {
      name: "Veterans Day",
      month: 11, day: 11, range: RANGE,
      accent: '#B22234', secondary: '#3C3B6E',
      heroGradient: 'from-[#B22234]/10 via-transparent to-[#3C3B6E]/10',
      label: 'VETERANS DAY',
      sectionAccent: { bg: 'rgba(178,34,52,0.12)', color: '#B22234', border: '#B22234' },
      lightAccent: { bg: 'rgba(178,34,52,0.1)', color: '#991b1b', border: '#dc2626' }
    },
    {
      name: "Thanksgiving",
      month: thanksgiving.month, day: thanksgiving.day, range: RANGE,
      accent: '#CD853F', secondary: '#8B4513',
      heroGradient: 'from-[#CD853F]/15 via-transparent to-[#8B4513]/10',
      label: 'HAPPY THANKSGIVING',
      sectionAccent: { bg: 'rgba(205,133,63,0.15)', color: '#CD853F', border: '#CD853F' },
      lightAccent: { bg: 'rgba(180,83,9,0.1)', color: '#92400e', border: '#b45309' }
    },
    {
      name: "Christmas",
      month: 12, day: 25, range: RANGE,
      accent: '#FF0000', secondary: '#006400',
      heroGradient: 'from-[#FF0000]/12 via-transparent to-[#006400]/12',
      label: 'MERRY CHRISTMAS',
      sectionAccent: { bg: 'rgba(255,0,0,0.12)', color: '#FF4444', border: '#FF0000' },
      lightAccent: { bg: 'rgba(22,163,74,0.1)', color: '#15803d', border: '#16a34a' }
    }
  ];
}

function getActiveHoliday() {
  const now = new Date();
  const year = now.getFullYear();
  const doy = Math.floor((now - new Date(year, 0, 0)) / 86400000);
  const holidays = buildHolidays(year);

  for (const holiday of holidays) {
    const holidayDate = new Date(year, holiday.month - 1, holiday.day);
    const holidayDoy = Math.floor((holidayDate - new Date(year, 0, 0)) / 86400000);
    const diff = doy - holidayDoy;

    if (diff >= holiday.range[0] && diff <= holiday.range[1]) {
      return holiday;
    }
  }

  // Also check next year's New Year (Dec range into Jan)
  const nyNextYear = buildHolidays(year + 1)[0];
  const nyDate = new Date(year + 1, 0, 1);
  const nyDoy = Math.floor((nyDate - new Date(year, 0, 0)) / 86400000);
  const diff = doy - nyDoy;
  if (diff >= nyNextYear.range[0] && diff <= nyNextYear.range[1]) {
    return nyNextYear;
  }

  return null;
}

export function useSeasonalTheme() {
  const holiday = useMemo(() => getActiveHoliday(), []);

  return {
    isHoliday: !!holiday,
    holiday,
    heroGradient: holiday?.heroGradient || null,
    label: holiday?.label || null,
    accent: holiday?.accent || null,
    secondary: holiday?.secondary || null,
    sectionAccent: holiday?.sectionAccent || null,
    lightAccent: holiday?.lightAccent || null,
  };
}
